import {Color} from '@benchling/nucleus-ui';
import {Button as MantineButton} from '@mantine/core';
import {DateInput, DatePicker, DatesRangeValue} from '@mantine/dates';
import {css, StyleSheet} from 'aphrodite';
import moment from 'moment';
import React, {useCallback, useState} from 'react';

import {FONT_PROXIMA_14} from 'styles/FontConstants';
import {MOMENT_DATE_FORMAT} from 'util/DateConstants';

export interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

export type FilterMode = 'between' | 'before' | 'after';

export const FILTER_MODE = {
  BETWEEN: 'between',
  BEFORE: 'before',
  AFTER: 'after',
} as const satisfies Record<string, FilterMode>;

export interface FlexibleDateRangePickerProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  mode: FilterMode;
  onModeChange: (mode: FilterMode) => void;
  displayDateFormat?: string;
}

function formatToISODate(date: Date | null): string | null {
  if (!date) {
    return null;
  }
  return moment(date).format(MOMENT_DATE_FORMAT);
}

function parseISODate(dateString: string | null): Date | null {
  if (!dateString) {
    return null;
  }
  const parsed = moment(dateString, MOMENT_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
}

export function isDateRangeValid(range: DateRange, mode: FilterMode): boolean {
  if (mode === FILTER_MODE.BETWEEN && range.startDate && range.endDate) {
    return range.startDate <= range.endDate;
  }
  return true;
}

const MODE_OPTIONS: Array<{value: FilterMode; label: string}> = [
  {value: FILTER_MODE.BETWEEN, label: 'Between'},
  {value: FILTER_MODE.BEFORE, label: 'Before'},
  {value: FILTER_MODE.AFTER, label: 'After'},
];

export default function FlexibleDateRangePicker({
  value,
  onChange,
  mode,
  onModeChange,
  displayDateFormat = MOMENT_DATE_FORMAT,
}: FlexibleDateRangePickerProps): JSX.Element {
  const [displayedDate, setDisplayedDate] = useState<Date>(
    parseISODate(value.startDate) ?? parseISODate(value.endDate) ?? new Date()
  );

  // Custom date parser that accepts multiple common formats while rejecting partial input.
  // Mantine's default parser falls back to `new Date(val)` which is overly lenient
  // (e.g. "0" evaluates to Jan 1 2000), causing spurious state updates and data fetches.
  const dateParser = useCallback(
    (val: string): Date | null => {
      const separators = ['/', '-', '.'];
      // By default we want to prioritize month first for ambiguous dates. But if the tenant uses a date format
      // that has day first, we should prioritize day first as well
      const isDayFirst = displayDateFormat.startsWith('D');
      const monthFirst = separators.map((s) => `M${s}D${s}YYYY`);
      const dayFirst = separators.map((s) => `D${s}M${s}YYYY`);
      const formats = [
        displayDateFormat,
        ...(isDayFirst ? [...dayFirst, ...monthFirst] : [...monthFirst, ...dayFirst]),
        ...separators.map((s) => `YYYY${s}MM${s}DD`),
        ...separators.map((s) => `YYYY${s}M${s}D`),
        'DDMMMYYYY',
        'DMMMYYYY',
      ];
      const parsed = moment(val, formats, true);
      return parsed.isValid() ? parsed.toDate() : null;
    },
    [displayDateFormat]
  );

  const parsedStartDate = parseISODate(value.startDate);
  const parsedEndDate = parseISODate(value.endDate);

  const dateOrderError = React.useMemo(() => {
    if (!isDateRangeValid(value, mode)) {
      return 'Start date must be before end date';
    }
    return undefined;
  }, [mode, value]);

  const handleModeChange = (newMode: FilterMode): void => {
    if (mode === newMode) {
      return;
    }
    onModeChange(newMode);

    if (newMode === FILTER_MODE.AFTER) {
      onChange({
        startDate: value.endDate,
        endDate: null,
      });
      setDisplayedDate(parsedEndDate || new Date());
    } else if (newMode === FILTER_MODE.BEFORE) {
      onChange({
        startDate: null,
        endDate: value.startDate,
      });
      setDisplayedDate(parsedStartDate || new Date());
    } else if (newMode === FILTER_MODE.BETWEEN) {
      onChange({
        startDate: null,
        endDate: null,
      });
      setDisplayedDate(new Date());
    }
  };

  const handleSingleDateChange = (date: Date | null): void => {
    const formattedDate = formatToISODate(date);
    if (mode === FILTER_MODE.AFTER) {
      onChange({startDate: formattedDate, endDate: null});
    } else if (mode === FILTER_MODE.BEFORE) {
      onChange({startDate: null, endDate: formattedDate});
    }
    if (date) {
      setDisplayedDate(date);
    }
  };

  const handleRangeChange = (range: DatesRangeValue): void => {
    const [startDate, endDate] = range;
    onChange({
      startDate: formatToISODate(startDate),
      endDate: formatToISODate(endDate),
    });
  };

  const handleStartInputChange = (date: Date | null): void => {
    onChange({
      startDate: formatToISODate(date),
      endDate: value.endDate,
    });
    if (date) {
      setDisplayedDate(date);
    }
  };

  const handleEndInputChange = (date: Date | null): void => {
    onChange({
      startDate: value.startDate,
      endDate: formatToISODate(date),
    });
    if (date) {
      setDisplayedDate(date);
    }
  };

  const rangeValue: DatesRangeValue = [parsedStartDate, parsedEndDate];
  const singleValue =
    mode === FILTER_MODE.AFTER ? parsedStartDate : mode === FILTER_MODE.BEFORE ? parsedEndDate : null;

  const uppercaseDates = displayDateFormat.includes('MMM');

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.dateInputContainer)}>
        {mode === FILTER_MODE.BETWEEN ? (
          <>
            <DateInput
              placeholder="Start date"
              value={parsedStartDate}
              onChange={handleStartInputChange}
              dateParser={dateParser}
              valueFormat={displayDateFormat}
              size="lg"
              error={Boolean(dateOrderError)}
              popoverProps={{withinPortal: true, opened: false}}
              styles={{
                input: {
                  borderWidth: '1px',
                  '&:not(:placeholder-shown)': {
                    textTransform: uppercaseDates ? 'uppercase' : undefined,
                  },
                  '&[data-error]': {
                    borderColor: Color.RED_500,
                  },
                },
              }}
              aria-label="Start date"
            />
            <span className={css(styles.dateSeparator)}>-</span>
            <DateInput
              placeholder="End date"
              value={parsedEndDate}
              onChange={handleEndInputChange}
              dateParser={dateParser}
              valueFormat={displayDateFormat}
              size="lg"
              error={Boolean(dateOrderError)}
              popoverProps={{withinPortal: true, opened: false}}
              styles={{
                input: {
                  borderWidth: '1px',
                  '&:not(:placeholder-shown)': {
                    textTransform: uppercaseDates ? 'uppercase' : undefined,
                  },
                  '&[data-error]': {
                    borderColor: Color.RED_500,
                  },
                },
              }}
              aria-label="End date"
            />
          </>
        ) : (
          <DateInput
            placeholder="Enter date"
            value={singleValue}
            onChange={handleSingleDateChange}
            dateParser={dateParser}
            valueFormat={displayDateFormat}
            size="lg"
            styles={{
              input: {
                borderWidth: '1px',
                '&:not(:placeholder-shown)': {
                  textTransform: uppercaseDates ? 'uppercase' : undefined,
                },
              },
            }}
            popoverProps={{withinPortal: true, opened: false}}
            aria-label={mode === FILTER_MODE.AFTER ? 'After date' : 'Before date'}
          />
        )}
      </div>
      {dateOrderError && <span className={css(styles.errorText)}>{dateOrderError}</span>}
      <div className={css(styles.datePickerContainer)}>
        <div className={css(styles.modeContainer)}>
          {MODE_OPTIONS.map((option) => (
            <MantineButton
              key={option.value}
              variant="ghost"
              onClick={() => {
                handleModeChange(option.value);
              }}
              aria-pressed={mode === option.value}
            >
              {option.label}
            </MantineButton>
          ))}
          <MantineButton
            variant="ghost"
            disabled={value.startDate == null && value.endDate == null}
            onClick={() => {
              onChange({startDate: null, endDate: null});
            }}
            styles={{
              root: {marginTop: 'auto'},
            }}
          >
            Reset
          </MantineButton>
        </div>
        <div className={css(styles.calendarContainer)}>
          {mode === FILTER_MODE.BETWEEN ? (
            <DatePicker
              key="range"
              type="range"
              value={rangeValue}
              onChange={handleRangeChange}
              date={displayedDate}
              onDateChange={setDisplayedDate}
              allowSingleDateInRange={true}
              size="xl"
            />
          ) : (
            <DatePicker
              key={mode}
              type="default"
              value={singleValue}
              onChange={handleSingleDateChange}
              date={displayedDate}
              onDateChange={setDisplayedDate}
              size="xl"
            />
          )}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  dateInputContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    alignItems: 'center',
    marginBottom: '16px',
  },
  datePickerContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modeContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  calendarContainer: {
    display: 'flex',
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${Color.GRAY_300}`,
  },
  dateSeparator: {
    color: Color.GRAY_400,
    fontSize: '13px',
  },
  errorText: {
    color: Color.RED_500,
    ...FONT_PROXIMA_14,
    marginTop: '-12px',
    marginBottom: '12px',
  },
});
